import { createContext, useContext, useEffect, useState } from 'react';

interface SiteConfig {
  production_domain: string;
  staging_domain: string;
  active_env: 'production' | 'staging';
  active_url: string;
  wc_proxy_url: string;
  cms_url: string;
}

interface CMSContextValue {
  data: Record<string, any>;
  config: SiteConfig | null;
  loading: boolean;
}

const DEFAULT_CONFIG: SiteConfig = {
  production_domain: 'https://phoenix.ge',
  staging_domain: 'https://phoenix.ge',
  active_env: 'staging',
  active_url: 'https://phoenix.ge',
  wc_proxy_url: '',
  cms_url: '',
};

const CMSContext = createContext<CMSContextValue>({ data: {}, config: null, loading: false });

export function CMSProvider({ children }: { children: React.ReactNode }) {
  const [data, setData]     = useState<Record<string, any>>({});
  const [config, setConfig] = useState<SiteConfig | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const url = import.meta.env.VITE_CMS_URL;
    if (!url) return;

    setLoading(true);

    // Fetch config and all CMS content in parallel
    Promise.all([
      fetch(`${url}/wp-json/phoenix-cms/v1/config`).then(r => r.json()).catch(() => null),
      fetch(`${url}/wp-json/phoenix-cms/v1/content`).then(r => r.json()).catch(() => null),
    ]).then(([cfg, content]) => {
      if (cfg) setConfig(cfg as SiteConfig);
      if (content) setData(content || {});
    }).finally(() => setLoading(false));
  }, []);

  return (
    <CMSContext.Provider value={{ data, config, loading }}>
      {children}
    </CMSContext.Provider>
  );
}

export function useCMS<T extends object>(section: string, fallback: T): T {
  const { data } = useContext(CMSContext);
  if (!data[section]) return fallback;
  return deepMerge(fallback, data[section]) as T;
}

/** Access site config (domains, active env, wc_proxy_url) */
export function useSiteConfig(): SiteConfig {
  const { config } = useContext(CMSContext);
  return config ?? DEFAULT_CONFIG;
}

function deepMerge(base: any, override: any): any {
  if (typeof base !== 'object' || base === null) return override ?? base;
  if (Array.isArray(base)) return Array.isArray(override) && override.length > 0 ? override : base;
  const result = { ...base };
  for (const key of Object.keys(override || {})) {
    if (override[key] !== undefined && override[key] !== '') {
      result[key] = deepMerge(base[key], override[key]);
    }
  }
  return result;
}
